import { useCallback, useContext, useMemo, useRef } from 'react';
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosRequestHeaders, AxiosResponse } from 'axios';
import { LoadingContext } from '../contexts/LoadingContext';
import { config } from '../config';
import { logger } from '../helpers/logger';
import { GlobalContext } from '../contexts/GlobalContext';
import { AuthContext } from '../contexts/AuthContext';
import { parseAccessToken } from '../helpers/parseAccessToken';
import { ITokenData } from './useAuth';
import { useHistory } from 'react-router-dom';
import { UnverifiedAccountError } from '../types/UnverifiedAccountError';

enum HttpResponseCode {
  Unauthorized = 401,
  Forbidden = 403,
}

const tokenRefreshEndpoint = '/identities/auth/refresh-token';

export interface IApiConfig {
  addAuthHeader?: boolean;
  logRequests?: boolean;
  indicateLoading?: boolean;
  signOutUnauthorized?: boolean;
  wrapUnverifiedAccountResponse?: boolean;
  throwIfErrorFieldIsPresent?: boolean;
  refreshTokenBeforeExpiration?: boolean;
  baseURL?: string;
}

export interface IApiExecutor {
  get: <ResponseType>(path: string, config?: AxiosRequestConfig) => Promise<AxiosResponse<ResponseType>>;
  post: <ResponseType>(path: string, data?: any, config?: AxiosRequestConfig) => Promise<AxiosResponse<ResponseType>>;
  put: <ResponseType>(path: string, data?: any, config?: AxiosRequestConfig) => Promise<AxiosResponse<ResponseType>>;
  patch: <ResponseType>(path: string, data?: any, config?: AxiosRequestConfig) => Promise<AxiosResponse<ResponseType>>;
  del: <ResponseType>(path: string, config?: AxiosRequestConfig) => Promise<AxiosResponse<ResponseType>>;
}

enum ETokenStatus {
  Okay,
  NeedsRefresh,
  TooLate,
}

const accessTokenRefreshMargin = 5 * 6e4;

class TokenRefreshController {
  private promise: Promise<ITokenData> | null = null;

  protected constructor(
    private baseURL: string,
    private tokenGetter: () => ITokenData | null,
    private tokenUpdater: (value: ITokenData) => void
  ) {}

  public checkStatus(tokenData: ITokenData): ETokenStatus {
    const parsed = parseAccessToken(tokenData.access_token);
    if (parsed.exp * 1e3 > Date.now() + accessTokenRefreshMargin) {
      return ETokenStatus.Okay;
    }
    const parsedRefresh = parseAccessToken(tokenData.refresh_token);
    if (parsedRefresh.exp * 1000 <= Date.now()) {
      return ETokenStatus.TooLate;
    }
    return ETokenStatus.NeedsRefresh;
  }

  public async requestTokenRefresh(tokenData: ITokenData): Promise<ITokenData> {
    if (!this.promise) {
      this.promise = new Promise<ITokenData>((resolve, reject) => {
        navigator.locks.request('refresh-token-lock', async () => {
          const currentTokenData = this.tokenGetter();

          if (!currentTokenData) {
            this.promise = null;
            return reject(new Error('no token data'));
          }

          const tokenStatus = this.checkStatus(currentTokenData);

          if (tokenStatus === ETokenStatus.Okay) {
            this.promise = null;
            return resolve(currentTokenData);
          }

          const refreshInstance = axios.create({
            baseURL: this.baseURL,
            headers: {},
          });

          try {
            const response = await refreshInstance.post(tokenRefreshEndpoint, tokenData);
            this.tokenUpdater(response.data.payload);
            this.promise = null;
            return resolve(response.data.payload);
          } catch (e) {
            this.promise = null;
            reject(e);
          }
        });
      });
    }
    return this.promise;
  }

  private static instance?: TokenRefreshController;

  public static getInstance(
    baseURL: string,
    tokenGetter: () => ITokenData | null,
    tokenUpdater: (value: ITokenData) => void
  ): TokenRefreshController {
    if (!this.instance) {
      this.instance = new this(baseURL, tokenGetter, tokenUpdater);
    }
    return this.instance;
  }
}

export const useApi = ({
  addAuthHeader = true,
  logRequests = true,
  indicateLoading = true,
  signOutUnauthorized = true,
  wrapUnverifiedAccountResponse = true,
  throwIfErrorFieldIsPresent = true,
  refreshTokenBeforeExpiration = true,
  baseURL,
}: IApiConfig = {}): IApiExecutor => {
  if (!addAuthHeader) {
    signOutUnauthorized = false;
    refreshTokenBeforeExpiration = false;
  }

  const history = useHistory();
  const { setSignInFastTrack } = useContext(GlobalContext);

  const { identity, updateToken, getToken, forgetCurrentIdentity } = useContext(AuthContext);

  const { startLoading, endLoading } = useContext(LoadingContext);

  const tokenDataRef = useRef<ITokenData | undefined>();
  tokenDataRef.current = identity?.tokenData;

  const refresher = TokenRefreshController.getInstance(
    baseURL || localStorage.getItem('apiUrl') || config.apiUrl,
    getToken,
    updateToken
  );

  const createAxiosInstance = useCallback(() => {
    const axiosInstance = axios.create({
      baseURL: baseURL || localStorage.getItem('apiUrl') || config.apiUrl,
    });

    return axiosInstance;
  }, [baseURL]);

  const addAuthHeaderInterceptor = useCallback((instance: AxiosInstance) => {
    const reqInterceptor = instance.interceptors.request.use((config) => {
      const authHeader: AxiosRequestHeaders = tokenDataRef.current?.access_token
        ? { Authorization: `Bearer ${tokenDataRef.current?.access_token}` }
        : {};

      return {
        ...config,
        headers: {
          ...config.headers,
          ...authHeader,
        },
      };
    });

    return () => {
      instance.interceptors.request.eject(reqInterceptor);
    };
  }, []);

  const addLoggingInterceptors = useCallback((instance: AxiosInstance) => {
    const reqInterceptor = instance.interceptors.request.use((config) => {
      logger.http(`[${config.method?.toUpperCase()}] REQUEST`, config.url, config);
      return config;
    });
    const resInterceptor = instance.interceptors.response.use(
      (response) => {
        logger.http(`[${response.config.method?.toUpperCase()}] RESPONSE`, response.config.url, response);
        return response;
      },
      (error: AxiosError) => {
        logger.http(
          `[${error.response?.config.method?.toUpperCase()}] RESPONSE ERROR`,
          error.response?.config?.url ?? error?.config?.url ?? 'unknown url',
          error.response
        );
        throw error;
      }
    );
    return () => {
      instance.interceptors.request.eject(reqInterceptor);
      instance.interceptors.response.eject(resInterceptor);
    };
  }, []);

  const addLoadingInterceptors = useCallback((instance: AxiosInstance) => {
    const reqInterceptor = instance.interceptors.request.use((config) => {
      startLoading();
      return config;
    });
    const resInterceptor = instance.interceptors.response.use(
      (response) => {
        endLoading();
        return response;
      },
      (error) => {
        endLoading();
        throw error;
      }
    );
    return () => {
      instance.interceptors.request.eject(reqInterceptor);
      instance.interceptors.response.eject(resInterceptor);
    };
  }, []);

  const addSignOutUnauthorizedInterceptor = useCallback(
    (instance: AxiosInstance) => {
      const resInterceptor = instance.interceptors.response.use(
        (response) => response,
        (error) => {
          if (error?.response?.status === HttpResponseCode.Unauthorized) {
            if (identity?.email) {
              setSignInFastTrack({
                email: identity.email,
              });
            }
            forgetCurrentIdentity();
          }
          throw error;
        }
      );
      return () => {
        instance.interceptors.response.eject(resInterceptor);
      };
    },
    [identity?.email]
  );

  const addWrapUnverifiedAccountResponse = useCallback(
    (instance: AxiosInstance) => {
      const resInterceptor = instance.interceptors.response.use(
        (response) => response,
        (error: AxiosError) => {
          if (
            error?.response?.status === HttpResponseCode.Forbidden &&
            error?.response?.data?.reason?.includes('[User email not verified]')
          ) {
            throw new UnverifiedAccountError();
          }
          throw error;
        }
      );
      return () => {
        instance.interceptors.response.eject(resInterceptor);
      };
    },
    [history?.push]
  );

  const addThrowIfErrorPropertyIsPresentInterceptor = useCallback((instance: AxiosInstance) => {
    const resInterceptor = instance.interceptors.response.use((response) => {
      if (response.data && typeof response.data === 'object' && 'error' in response.data && !!response.data.error) {
        throw response.data.error;
      }
      return response;
    });
    return () => {
      instance.interceptors.response.eject(resInterceptor);
    };
  }, []);

  const addRefreshTokenBeforeExpirationInterceptor = useCallback((instance: AxiosInstance) => {
    const reqInterceptor = instance.interceptors.request.use(async (reqConfig) => {
      if (reqConfig.url?.includes(tokenRefreshEndpoint)) {
        return reqConfig;
      }

      if (tokenDataRef.current) {
        const tokenStatus = refresher.checkStatus(tokenDataRef.current);

        if (tokenStatus === ETokenStatus.NeedsRefresh) {
          const newTokenData = await refresher.requestTokenRefresh(tokenDataRef.current);

          return {
            ...reqConfig,
            headers: {
              ...reqConfig.headers,
              Authorization: `Bearer ${newTokenData.access_token}`,
            },
          };
        }
      }
      return reqConfig;
    });

    return () => {
      instance.interceptors.request.eject(reqInterceptor);
    };
  }, []);

  const axiosInstance = useMemo(() => {
    const instance = createAxiosInstance();
    logRequests && addLoggingInterceptors(instance);
    indicateLoading && addLoadingInterceptors(instance);
    signOutUnauthorized && addSignOutUnauthorizedInterceptor(instance);
    wrapUnverifiedAccountResponse && addWrapUnverifiedAccountResponse(instance);
    throwIfErrorFieldIsPresent && addThrowIfErrorPropertyIsPresentInterceptor(instance);
    refreshTokenBeforeExpiration && addRefreshTokenBeforeExpirationInterceptor(instance);
    /** The last added interceptor is called first. AuthHeader interceptor needs to be executed before refresh token
     * interceptor, because that interceptor may need to replace Authorization header */
    addAuthHeader && addAuthHeaderInterceptor(instance);
    return instance;
  }, [
    createAxiosInstance,
    addAuthHeaderInterceptor,
    addLoggingInterceptors,
    addLoadingInterceptors,
    addSignOutUnauthorizedInterceptor,
    addWrapUnverifiedAccountResponse,
    addThrowIfErrorPropertyIsPresentInterceptor,
    addRefreshTokenBeforeExpirationInterceptor,
    addAuthHeader,
    logRequests,
    indicateLoading,
    signOutUnauthorized,
  ]);

  const get = useCallback(
    <T>(path: string, config?: AxiosRequestConfig) => {
      return axiosInstance.get<T, AxiosResponse<T>>(path, config);
    },
    [axiosInstance]
  );

  const post = useCallback(
    <T>(path: string, data?: any, config?: AxiosRequestConfig) => {
      return axiosInstance.post<T, AxiosResponse<T>>(path, data, config);
    },
    [axiosInstance]
  );

  const put = useCallback(
    <T>(path: string, data?: any, config?: AxiosRequestConfig) => {
      return axiosInstance.put<T, AxiosResponse<T>>(path, data, config);
    },
    [axiosInstance]
  );

  const patch = useCallback(
    <T>(path: string, data?: any, config?: AxiosRequestConfig) => {
      return axiosInstance.patch<T, AxiosResponse<T>>(path, data, config);
    },
    [axiosInstance]
  );

  const del = useCallback(
    <T>(path: string, config?: AxiosRequestConfig) => {
      return axiosInstance.delete<T, AxiosResponse<T>>(path, config);
    },
    [axiosInstance]
  );

  return useMemo(
    () => ({
      get,
      post,
      put,
      patch,
      del,
    }),
    [get, post, put, patch, del]
  );
};
